import { withErrorBoundary } from 'app/components/error-boundary';
import { Column, Row } from 'app/design/primitives/box';
import { Page } from 'app/design/primitives/page';
import { Switch } from 'app/design/primitives/switch';
import { Text } from 'app/design/primitives/text';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useNavigationOptions } from 'app/hooks/navigation';
import { withDashboardLayout } from 'app/hooks/with-dashboard-layout';
import { useActivitySectors, useToggleActivitySector } from 'app/network/groups';
import { useTheme } from 'app/shopify';
import type { NavigationScreen } from 'app/types/navigation';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import { usePageTitle } from 'app/utils/web-helpers';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import Toast from 'react-native-toast-message';

const ActivitySectorSelectorScreenComponent: NavigationScreen<'activity-sectors'> = ({ navigation }) => {
  const theme = useTheme();
  const { t } = useScopedTranslation();

  const { data: sectors, isPending, isLoading } = useActivitySectors();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const pendingIdRef = useRef<string | null>(null);
  const toggle = useToggleActivitySector({
    onError: () => {
      setPendingId(null);
      pendingIdRef.current = null;
      Toast.show({ type: 'error', position: 'bottom', bottomOffset: 100, text1: t('errorToast') });
    },
  });

  useNavigationOptions(navigation, {
    headerTitle: () => (
      <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
        {t('title')}
      </Text>
    ),
  });

  const setPageTitle = usePageTitle('activity-sectors');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const selectedCount = (sectors ?? []).filter((s) => s.selected).length;

  const onToggle = (groupId: string, selected: boolean) => {
    if (pendingIdRef.current) return;
    if (selected && selectedCount <= 1) {
      Toast.show({ type: 'error', position: 'bottom', bottomOffset: 100, text1: t('minOneToast') });
      return;
    }
    pendingIdRef.current = groupId;
    setPendingId(groupId);
    toggle.mutate(
      { groupId, selected },
      {
        onSettled: () => {
          pendingIdRef.current = null;
          setPendingId((current) => (current === groupId ? null : current));
        },
      },
    );
  };

  const loading = isPending || isLoading;
  const isEmpty = !loading && sectors?.length === 0;

  let content: ReactNode;
  if (loading) {
    content = (
      <Column gap="spacing.general.x2">
        {[0, 1, 2].map((i) => (
          <SectorRowSkeleton key={i} />
        ))}
      </Column>
    );
  } else if (isEmpty) {
    content = (
      <Column alignItems="center" marginTop="spacing.general.x12" gap="spacing.general.x2">
        <Text color="text.secondary" fontSize={15} textAlign="center" variant="bodyBase">
          {t('empty')}
        </Text>
      </Column>
    );
  } else {
    content = (
      <Column gap="spacing.general.x2">
        {sectors?.map((sector) => {
          const isPendingRow = pendingId === sector.id;
          return (
            <Pressable
              accessibilityRole="switch"
              accessibilityState={{ checked: sector.selected, busy: isPendingRow }}
              disabled={isPendingRow}
              key={sector.id}
              onPress={() => onToggle(sector.id, sector.selected)}
              style={({ pressed }) => ({
                paddingHorizontal: theme.spacing['spacing.general.x4'],
                paddingVertical: theme.spacing['spacing.general.x3'],
                backgroundColor: theme.colors['surface.tertiary-neutral'],
                borderRadius: theme.borderRadii['radius.md'],
                opacity: pressed || isPendingRow ? 0.7 : 1,
              })}
            >
              <Row alignItems="center" gap="spacing.general.x4">
                <Text flex={1} fontSize={16} fontWeight="600" numberOfLines={2}>
                  {sector.name}
                </Text>
                {isPendingRow ? (
                  <ActivityIndicator color={theme.colors['surface.brand.default']} size="small" />
                ) : (
                  <Switch onValueChange={() => onToggle(sector.id, sector.selected)} value={sector.selected} />
                )}
              </Row>
            </Pressable>
          );
        })}
      </Column>
    );
  }

  return (
    <Page title={t('title')}>
      <Text color="text.secondary" fontSize={14} marginBottom="spacing.general.x4" variant="bodyBase">
        {t('subtitle')}
      </Text>

      {content}
    </Page>
  );
};

export const ActivitySectorSelectorScreen: NavigationScreen<'activity-sectors'> = withOptionalMetrics(
  'ActivitySectorSelectorScreen',
  withErrorBoundary(withDashboardLayout(ActivitySectorSelectorScreenComponent)),
);

function SectorRowSkeleton() {
  const theme = useTheme();
  return (
    <View
      style={{
        height: 64,
        paddingHorizontal: theme.spacing['spacing.general.x4'],
        backgroundColor: theme.colors['surface.tertiary-neutral'],
        borderRadius: theme.borderRadii['radius.md'],
      }}
    />
  );
}
